// プレイヤー：4方向移動＋歩行アニメ＋剣攻撃＋ダッシュ
import Phaser from 'phaser';
import { bus } from '../systems/EventBus';
import { Dir4, DIR_VECTORS, vectorToDir } from '../data/Directions';

const BASE_SPEED = 110;
const DASH_SPEED = 220;
const DASH_DURATION_MS = 200;
const DASH_COOLDOWN_MS = 600;
const INVULNERABLE_MS = 700;
const ATTACK_DURATION_MS = 240; // 攻撃フレーム表示時間
const ATTACK_COOLDOWN_MS = 350;

export class Player extends Phaser.Physics.Arcade.Sprite {
  public maxHp = 10; // 1ハート=2 → 5ハート
  public hp = 10;
  public facing: Dir4 = 'down';

  // 状態フラグ
  public isAttacking = false;
  public isDashing = false;
  private invulnerableMs = 0;
  private dashTimer = 0;
  private dashCooldown = 0;
  private attackTimer = 0;
  private attackCooldown = 0;

  // 入力
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
  };
  private spaceKey: Phaser.Input.Keyboard.Key;
  private shiftKey: Phaser.Input.Keyboard.Key;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'player_walk', 1); // 初期フレーム=下向きの idle
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setCollideWorldBounds(true);
    // 物理ボディは絵より少し小さくして見栄えを良くする
    (this.body as Phaser.Physics.Arcade.Body).setSize(16, 16).setOffset(8, 14);

    this.cursors = scene.input.keyboard!.createCursorKeys();
    this.wasd = {
      W: scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      A: scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      S: scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
    this.spaceKey = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.shiftKey = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
  }

  override update(_time: number, delta: number): void {
    // タイマー減衰
    this.invulnerableMs = Math.max(0, this.invulnerableMs - delta);
    this.dashCooldown = Math.max(0, this.dashCooldown - delta);
    this.attackCooldown = Math.max(0, this.attackCooldown - delta);

    // 攻撃フレーム終了
    if (this.isAttacking) {
      this.attackTimer -= delta;
      if (this.attackTimer <= 0) this.isAttacking = false;
    }
    // ダッシュ終了
    if (this.isDashing) {
      this.dashTimer -= delta;
      if (this.dashTimer <= 0) this.isDashing = false;
    }

    // 入力：移動ベクトル
    let vx = 0;
    let vy = 0;
    if (this.cursors.left.isDown || this.wasd.A.isDown) vx -= 1;
    if (this.cursors.right.isDown || this.wasd.D.isDown) vx += 1;
    if (this.cursors.up.isDown || this.wasd.W.isDown) vy -= 1;
    if (this.cursors.down.isDown || this.wasd.S.isDown) vy += 1;

    // ダッシュ開始判定（移動方向があり、クールダウン明け）
    if (
      Phaser.Input.Keyboard.JustDown(this.shiftKey) &&
      this.dashCooldown <= 0 &&
      !this.isDashing &&
      (vx !== 0 || vy !== 0)
    ) {
      this.isDashing = true;
      this.dashTimer = DASH_DURATION_MS;
      this.dashCooldown = DASH_COOLDOWN_MS;
      this.invulnerableMs = Math.max(this.invulnerableMs, DASH_DURATION_MS);
    }

    // 速度適用：攻撃中は移動停止、ダッシュ中は加速
    const speed = this.isAttacking ? 0 : this.isDashing ? DASH_SPEED : BASE_SPEED;
    const v = new Phaser.Math.Vector2(vx, vy).normalize().scale(speed);
    this.setVelocity(v.x, v.y);

    // 向き更新（移動入力があれば）
    this.facing = vectorToDir(vx, vy, this.facing);

    // 攻撃開始判定
    if (
      Phaser.Input.Keyboard.JustDown(this.spaceKey) &&
      this.attackCooldown <= 0 &&
      !this.isAttacking
    ) {
      this.isAttacking = true;
      this.attackTimer = ATTACK_DURATION_MS;
      this.attackCooldown = ATTACK_COOLDOWN_MS;
      // 攻撃フレーム＝向きに応じて切替
      this.setTexture('player_attack', dirToAttackFrame(this.facing));
    } else if (this.isAttacking) {
      // 攻撃中は静止画キープ
    } else {
      // 通常時：歩行アニメ or 静止フレーム
      this.setTexture('player_walk');
      if (Math.abs(vx) > 0.01 || Math.abs(vy) > 0.01) {
        const animKey = `walk-${this.facing}`;
        if (this.anims.currentAnim?.key !== animKey) {
          this.anims.play(animKey, true);
        }
      } else {
        // 静止：歩行アニメの中央フレーム（idle）に固定
        this.anims.stop();
        this.setFrame(idleFrameOf(this.facing));
      }
    }

    // 被弾点滅
    if (this.invulnerableMs > 0) {
      this.setAlpha(Math.floor(this.invulnerableMs / 80) % 2 === 0 ? 0.5 : 1);
    } else {
      this.setAlpha(1);
    }
  }

  // ダメージ処理（amount = 体力ポイント、1ハート=2pt）
  takeDamage(amount: number, knockbackX = 0, knockbackY = 0): void {
    if (this.invulnerableMs > 0 || this.hp <= 0) return;
    this.hp = Math.max(0, this.hp - amount);
    this.invulnerableMs = INVULNERABLE_MS;
    bus.emit('player-hp', { hp: this.hp, maxHp: this.maxHp });
    if (knockbackX !== 0 || knockbackY !== 0) {
      this.setVelocity(knockbackX, knockbackY);
    }
    if (this.hp <= 0) {
      bus.emit('player-died', undefined);
    }
  }

  // ハートピース取得：最大HP+2（半ハート×2＝1ハート増）
  applyMaxHpUp(amount: number): void {
    this.maxHp += amount;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    bus.emit('player-hp', { hp: this.hp, maxHp: this.maxHp });
  }

  // 回復
  heal(amount: number): void {
    this.hp = Math.min(this.maxHp, this.hp + amount);
    bus.emit('player-hp', { hp: this.hp, maxHp: this.maxHp });
  }
}

// 歩行スプライトの静止フレーム＝各行の中央（idle/center）
function idleFrameOf(dir: Dir4): number {
  switch (dir) {
    case 'down':
      return 1;
    case 'left':
      return 4;
    case 'right':
      return 7;
    case 'up':
      return 10;
  }
}

// 攻撃スプライトの方向別フレーム（列順: down, left, right, up）
function dirToAttackFrame(dir: Dir4): number {
  switch (dir) {
    case 'down':
      return 0;
    case 'left':
      return 1;
    case 'right':
      return 2;
    case 'up':
      return 3;
  }
}

// プレイヤーの歩行アニメを Boot で定義する用のヘルパ
export function defineWalkAnimations(scene: Phaser.Scene): void {
  const dirs: { name: string; start: number }[] = [
    { name: 'walk-down', start: 0 },
    { name: 'walk-left', start: 3 },
    { name: 'walk-right', start: 6 },
    { name: 'walk-up', start: 9 },
  ];
  for (const { name, start } of dirs) {
    if (scene.anims.exists(name)) continue;
    scene.anims.create({
      key: name,
      frames: scene.anims.generateFrameNumbers('player_walk', {
        start,
        end: start + 2,
      }),
      frameRate: 8,
      repeat: -1,
    });
  }
}

// ヒット時にプレイヤーから「攻撃方向」と「攻撃判定矩形」を引き出す
export function getAttackHitbox(
  player: Player,
): Phaser.Geom.Rectangle {
  const range = 26; // 攻撃リーチ
  const w = 28;
  const h = 28;
  const vec = DIR_VECTORS[player.facing];
  const cx = player.x + vec.x * range;
  const cy = player.y + vec.y * range;
  return new Phaser.Geom.Rectangle(cx - w / 2, cy - h / 2, w, h);
}
